import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  HelpOutline as HelpIcon,
  RocketLaunch as OnboardingIcon,
  Tour as TourIcon,
  NewReleases as WhatsNewIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useOnboarding } from '../../onboarding/OnboardingProvider';
import { useRBAC } from '../../auth/useRBAC';
import { pickTourForRole } from '../../onboarding/tours';
import { skipWhenOffline } from '../../offline';

interface HelpDropdownProps {
  onOpenWhatsNew: () => void;
}

const HelpDropdown: React.FC<HelpDropdownProps> = ({ onOpenWhatsNew }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { openDrawer: openOnboardingDrawer, startTour, state } = useOnboarding();
  const { claims } = useRBAC();
  const { enqueueSnackbar } = useSnackbar();
  const roleTour = pickTourForRole(claims?.role);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenOnboarding = () => {
    // Cold-offline with no cached onboarding state: the drawer would render
    // nothing, making this a silent dead click — explain instead.
    if (!state && !skipWhenOffline()) {
      enqueueSnackbar('Setup checklist is unavailable offline', { variant: 'info' });
      handleClose();
      return;
    }
    openOnboardingDrawer();
    handleClose();
  };

  const handleReplayTour = () => {
    if (roleTour) startTour(roleTour.id);
    handleClose();
  };

  const handleOpenWhatsNew = () => {
    onOpenWhatsNew();
    handleClose();
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        className="help-tour-button"
        sx={{ mr: 2 }}
        aria-label="Help menu"
        aria-controls={open ? 'help-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <HelpIcon />
      </IconButton>
      
      <Menu
        id="help-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'help-button',
        }}
        PaperProps={{
          sx: {
            minWidth: 180,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem onClick={handleOpenOnboarding}>
          <ListItemIcon>
            <OnboardingIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Getting started" />
        </MenuItem>
        {roleTour && (
          <MenuItem onClick={handleReplayTour}>
            <ListItemIcon>
              <TourIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Replay welcome tour" />
          </MenuItem>
        )}
        <MenuItem onClick={handleOpenWhatsNew}>
          <ListItemIcon>
            <WhatsNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="What's new" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default HelpDropdown;
